// @flow
/** @jsx h */
import { h } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { useRequest } from '../../../api/services/useRequest';
import {
  salesPreCheckEligibility,
  sendOtpAPI,
  verifyOtpAPI,
} from '../../../api/controllers/salesController';
import EnterMobileNumberScreen from '../EnterMobileNumberScreen';
import EnterOtpScreen from '../EnterOtpScreen';
import {
  useSalesContext,
  useSalesDispatchContext,
} from '../../../storeProviders/salesAppStore';
import {
  salesLendersListsRoute,
  salesPreCheckEligibilityRoute,
} from '../../../alias/salesAppRoutes';
import useSalesAppError from '../hooks/useSalesAppError';
import { styled } from 'goober';
import ToastBox from '../ToastBox';
import { custPreApprovalMsgOverLayHeight } from '../constants';
import saRoute from '../../salesApp/constants/saRoute';
import useKeyEnter from '../../../hooks/useKeyEnter';
import { useSalesAppTracker } from '../tracker';
import { getSessionStorage, setSessionStorage } from '../../../utils';

const ToastContainer = styled('div')`
  position: absolute;
  top: -8px;
  width: 100%;
  transform: translateY(-100%);
`;

const MobileVerification = () => {
  const [mobileNo, setMobileNo] = useState({ mobileNumber: '' });
  const [otpValue, setOtpValue] = useState({ otpValue: '' });
  const salesAppTracker = useSalesAppTracker();
  const salesState = useSalesContext();
  const salesDispatch = useSalesDispatchContext();
  const {
    bottomSheetConfig,
    showInterstitial,
    selectedLenderDetails,
    routeDetails,
    singleFunctionality,
    isPreCheckEligibilityDone,
    amount,
    preCheckEligibilityMobileNumber,
    showOtpScreen,
  } = salesState;
  const { routeOnCompletion } = bottomSheetConfig;
  const { path } = routeDetails;

  const { setSalesAppErrorModal, salesAppError } = useSalesAppError();

  const sendOtpRequest = useRequest();
  const verifyOtpRequest = useRequest();
  const preCheckEligibilityRequest = useRequest();

  const [openToast, setOpenToast] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState(null);
  const verifiedEntity = getSessionStorage('verifiedEntity') || [];
  const isMobileVerified =
    mobileNo.mobileNumber.length === 10 &&
    verifiedEntity.includes(btoa(mobileNo.mobileNumber));

  const setVerifiedMobileInSessionStorage = (encodedMobile) => {
    const verifiedMobileNumbers = getSessionStorage('verifiedEntity');
    const mobileNumbers = verifiedMobileNumbers
      ? JSON.parse(verifiedMobileNumbers)
      : [];
    const updatedList = [...mobileNumbers, encodedMobile];
    const updatedMobileNumberList = JSON.stringify(updatedList);
    setSessionStorage('verifiedEntity', updatedMobileNumberList);
  };

  const handleResetMobile = useCallback(() => {
    salesDispatch({
      type: 'STORE_STATE',
      payload: {
        isPreCheckEligibilityDone: false,
        showOtpScreen: false,
      },
    });

    setOtpValue({ otpValue: '' });
  }, []);

  const verifyOtpSuccess = () => {
    salesDispatch({
      type: 'STORE_STATE',
      payload: {
        bottomSheetConfig: {
          showOverlay: false,
        },
      },
    });
    showInterstitial &&
      selectedLenderDetails &&
      salesDispatch({
        type: 'STORE_STATE',
        payload: {
          bottomSheetConfig: {
            showOverlay: true,
            moduleName: 'SHOW_INTERSTITIAL_MSG',
            overlayHeight: custPreApprovalMsgOverLayHeight,
          },
        },
      });
    if (routeOnCompletion) {
      path === '/sales-lender-details'
        ? window.history.back()
        : saRoute(
            salesLendersListsRoute.path,
            singleFunctionality,
            path === '/sa-pre-eligibility-check',
          );
    }
  };

  const sendOtpHandler = () => {
    if (isMobileVerified) {
      verifyOtpSuccess();
    } else {
      sendOtpAPI(
        sendOtpRequest.makeRequest,
        preCheckEligibilityMobileNumber,
      ).then((res) => {
        if (res.success) {
          salesAppTracker.trackPageView('sa_pv_share_code_sent');
          setSalesAppErrorModal({ customMsg: true });
        }
      });
      setOtpValue({ otpValue: '' });
    }
  };

  useEffect(() => {
    if (sendOtpRequest.error) {
      setOpenToast(true);
      setSalesAppErrorModal({ ...sendOtpRequest.error, customMsg: true });
    }
  }, [sendOtpRequest.error]);

  const preCheckEligibilityHandler = () => {
    if (isMobileVerified) {
      salesDispatch({
        type: 'STORE_STATE',
        payload: {
          mobile: mobileNo.mobileNumber,
        },
      });
      verifyOtpSuccess();
    } else {
      salesPreCheckEligibility(preCheckEligibilityRequest.makeRequest, {
        mobile: mobileNo.mobileNumber,
        amount,
      });
    }
  };

  useEffect(() => {
    if (preCheckEligibilityRequest.value) {
      setSalesAppErrorModal({ customMsg: true });

      salesDispatch({
        type: 'STORE_STATE',
        payload: {
          bottomSheetConfig: {
            showOverlay: false,
          },
          preCheckEligibilityResponse: preCheckEligibilityRequest.value,
          isPreCheckEligibilityDone: true,
          preCheckEligibilityMobileNumber: mobileNo.mobileNumber,
          amount: amount,
        },
      });

      saRoute(
        salesPreCheckEligibilityRoute.path,
        singleFunctionality,
        path === '/sales-lenders-list' ||
          path === '/sales-lender-details' ||
          path === '/sa-pre-eligibility-check',
      );
    }
  }, [preCheckEligibilityRequest.value]);

  useEffect(() => {
    if (preCheckEligibilityRequest.error) {
      setOpenToast(true);
      setSalesAppErrorModal({
        ...preCheckEligibilityRequest.error,
        customMsg: true,
      });
    }
  }, [preCheckEligibilityRequest.error]);

  useKeyEnter(() => {
    if (
      mobileNo.mobileNumber.length === 10 &&
      !preCheckEligibilityRequest.loading
    ) {
      preCheckEligibilityHandler();
    }
  });

  const verifyOtpHandler = () => {
    verifyOtpAPI(
      verifyOtpRequest.makeRequest,
      preCheckEligibilityMobileNumber,
      otpValue.otpValue,
    ).then((res) => {
      if (res.success) {
        salesAppTracker.trackPageView('sa_pv_share_code_verification_success');
      } else {
        salesAppTracker.trackPageView('sa_pv_share_code_verification_failed');
      }
    });
  };

  useEffect(() => {
    if (verifyOtpRequest.value) {
      setSalesAppErrorModal({ customMsg: true });
      setVerifiedMobileInSessionStorage(btoa(preCheckEligibilityMobileNumber));

      salesDispatch({
        type: 'STORE_STATE',
        payload: {
          mobile: preCheckEligibilityMobileNumber,
        },
      });

      verifyOtpSuccess();
    }
  }, [verifyOtpRequest.value]);

  useEffect(() => {
    if (verifyOtpRequest.error) {
      if (verifyOtpRequest.error.metadata) {
        setRemainingAttempts(
          parseInt(verifyOtpRequest.error.metadata.remainingShareCodeAttempts),
        );
      }
      setOpenToast(true);
      setSalesAppErrorModal({ ...verifyOtpRequest.error, customMsg: true });
    }
  }, [verifyOtpRequest.error]);

  useKeyEnter(() => {
    if (otpValue.otpValue.length === 5 && !verifyOtpRequest.loading) {
      verifyOtpHandler();
    }
  });

  return (
    <div>
      {!!salesAppError && salesAppError.errorCode && (
        <ToastContainer onClick={() => setOpenToast(false)}>
          <ToastBox
            msg={salesAppError.message || salesAppError.title}
            autoHideduration={5000}
            open={openToast}
            setOpen={setOpenToast}
          />
        </ToastContainer>
      )}
      {!isPreCheckEligibilityDone && !showOtpScreen ? (
        <EnterMobileNumberScreen
          mobileNo={mobileNo.mobileNumber}
          btnTxt={
            isMobileVerified
              ? 'Check Eligibility'
              : 'Check Customer’s Affordability'
          }
          btnHandler={preCheckEligibilityHandler}
          onChangeHandler={(mobileNumber) => {
            setMobileNo({ mobileNumber: mobileNumber });
          }}
          loading={preCheckEligibilityRequest.loading}
          isMobileVerified={isMobileVerified}
        />
      ) : (
        <EnterOtpScreen
          mobileNo={preCheckEligibilityMobileNumber}
          handleChangeMobile={handleResetMobile}
          btnHandler={verifyOtpHandler}
          resendOtpHandler={sendOtpHandler}
          btnTxt={'Verify Secure Share Code'}
          onChangeHandler={(otpValue) => {
            setOtpValue({ otpValue: otpValue });
          }}
          otpValue={otpValue.otpValue}
          loading={verifyOtpRequest.loading}
          openToast={setOpenToast}
          remainingAttempts={remainingAttempts}
        />
      )}
    </div>
  );
};

export default MobileVerification;



// WEBPACK FOOTER //
// ./components/salesApp/common/MobileVerification.js